import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import { findStoreIds } from "../../../../api_calls/utils";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";
import { checkUserRole } from "../../../../api_calls/userManagement";

const CampaignStoreSingleSelectFilter = ({
  reduxKeywordToSaveValue,
  filterParamRegionName,
  filterParamNielsenRegionCode,
  regionFilterEnabled,
  nielsenRegionFilterEnabled
}) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [storeList, setStoreList] = React.useState([]);
  const [selectedStore, setSelectedStore] = React.useState(null);
  const [dataLoaded, setDataLoaded] = React.useState(false);

  // determine user role (retailer vs brand)
  const userRole =
    storeDetails?.brand_report_view === true
      ? { user_role: "Brand" }
      : checkUserRole();

  // identical filtering logic to CampaignStoresFilter
  const filterStoreOptions = () => {
    const regionsSelected =
      storeDetails?.[reduxKeywordToSaveValue]?.[filterParamRegionName] || [];
    const nielsenRegionsSelected =
      storeDetails?.[reduxKeywordToSaveValue]?.[filterParamNielsenRegionCode] ||
      [];

    // start with campaign stores (or brand stores for brand users)
    let campaignStores =
      storeDetails.campaign_detail?.campaign_stores?.length > 0
        ? [...storeDetails.campaign_detail.campaign_stores].sort()
        : [];
    if (userRole.user_role === "Brand") {
      campaignStores = storeDetails.brand_filters?.impression_filter?.stores?.length
        ? [...storeDetails.brand_filters.impression_filter.stores].sort()
        : [];
    }

    // no region or Nielsen region filters enabled – return full list
    if (!regionFilterEnabled && !nielsenRegionFilterEnabled) {
      return campaignStores.sort();
    }

    // region filter only
    if (regionFilterEnabled && regionsSelected.length > 0) {
      return storeDetails.stores_list
        ? storeDetails.stores_list
            .filter(
              (store) =>
                campaignStores.includes(store.name) &&
                regionsSelected.includes(store.region.name)
            )
            .map((store) => store.name).sort()
        : [];
    }

    // Nielsen region filter only (when region filter is disabled)
    if (
      nielsenRegionFilterEnabled &&
      !regionFilterEnabled &&
      nielsenRegionsSelected.length > 0
    ) {
      return storeDetails.stores_list
        ? storeDetails.stores_list
            .filter(
              (store) =>
                campaignStores.includes(store.name) &&
                nielsenRegionsSelected.includes(store.region.nielsenRegion)
            )
            .map((store) => store.name).sort()
        : [];
    }

    // fallback to full list if no filter matches
    return campaignStores;
  };

  // convert storeList into SingleSelect options
  const storeOptions = React.useMemo(
    () => storeList.map((n) => ({ value: n, label: n })),
    [storeList]
  );

  // initial load: compute stores and set first option
  React.useEffect(() => {
    const list = filterStoreOptions();
    setStoreList(list);
    if (list.length > 0) {
      // pick first store and update redux
      const first = list[0];
      const selectedOpt = { value: first, label: first };
      setSelectedStore(selectedOpt);
      const storeIds = findStoreIds([first], false);
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: storeIds,
            selected_store_names: [selectedOpt],
          },
        })
      );
    } else {
      setSelectedStore(null);
    }
    setDataLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update options and selection when region/Nielsen filters change
  React.useEffect(() => {
    const list = filterStoreOptions();
    
    if (list.length > 0) {
      setStoreList(list);
      const first = list[0];
      const selectedOpt = { value: first, label: first };
      setSelectedStore(selectedOpt);
      const storeIds = findStoreIds([first], false);
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: storeIds,
            selected_store_names: [selectedOpt],
          },
        })
      );
    } else {
      setStoreList([]);
      setSelectedStore(null);
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: [],
            selected_store_names: [],
          },
        })
      );
    }
    // watch only the region/nielsen selections
  }, [
    storeDetails?.[reduxKeywordToSaveValue]?.[filterParamRegionName],
    storeDetails?.[reduxKeywordToSaveValue]?.[filterParamNielsenRegionCode],
  ]);

  // callback when user selects a different store
  const handleOnChange = (option) => {
    if (option) {
      setSelectedStore(option);
      const storeIds = findStoreIds([option.label], false);
      dispatchAction(
        updateStateData({
          [reduxKeywordToSaveValue]: {
            ...storeDetails[reduxKeywordToSaveValue],
            store_ids: storeIds,
            selected_store_names: [option],
          },
        })
      );
    }
  };

  return dataLoaded ? (
    <SingleSelect
      // use a key so the component remounts when options change
      key={storeOptions.map((o) => o.label).join("-")}
      enableFullWidth={false}
      label={"Store"}
      options={storeOptions}
      defaultVal={selectedStore}
      selectionCheck={(option, value) => option.label === value.label}
      onChangeCall={handleOnChange}
    />
  ) : null;
};

export default CampaignStoreSingleSelectFilter;
